import { useState, useCallback, useEffect, useRef } from 'react';

/** Cadence while restore progress keeps changing. */
const RESTORE_POLL_BASE_MS = 4000;
/** Ceiling once progress stops changing. */
const RESTORE_POLL_MAX_MS = 20000;
/** Multiplier applied to the interval on each unchanged response. */
const RESTORE_POLL_BACKOFF = 1.6;

export interface RestoreJobState {
  id: string | null;
  status: 'PENDING' | 'RUNNING' | 'VERIFYING' | 'COMPLETED' | 'FAILED' | null;
  progress: number;
  currentFile: string | null;
  filesRestored: number;
  dirsCreated: number;
  totalFiles: number;
  bytesWritten: number;
  error: string | null;
  isLoading: boolean;
  duration: number | null;
}

export type RestoreMode = 'file' | 'direct';

export interface PostgreSQLTargetConfig {
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
}

interface CreateRestoreRequest {
  backup_job_id: string;
  restore_location: 'original' | 'custom';
  custom_path?: string;
  overwrite: boolean;
  verify?: boolean;
  selected_files?: string[];
  restore_mode?: RestoreMode;
  postgresql?: PostgreSQLTargetConfig;
}

const initialState: RestoreJobState = {
  id: null,
  status: null,
  progress: 0,
  currentFile: null,
  filesRestored: 0,
  dirsCreated: 0,
  totalFiles: 0,
  bytesWritten: 0,
  error: null,
  isLoading: false,
  duration: null,
};

export function useRestoreJobPolling() {
  const [state, setState] = useState<RestoreJobState>(initialState);
  const pollIntervalRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // Detaches the visibility listener installed by startPolling.
  const stopListenersRef = useRef<(() => void) | null>(null);

  /** Seconds elapsed since polling started, for the modal's duration display. */
  const getElapsedTime = useCallback(() => {
    if (!startTimeRef.current) return 0;
    return Math.floor((Date.now() - startTimeRef.current) / 1000);
  }, []);

  const stopPolling = useCallback(() => {
    stopListenersRef.current?.();
    stopListenersRef.current = null;
    clearTimeout(pollIntervalRef.current);
    pollIntervalRef.current = undefined;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Stop polling if the component using this hook unmounts mid-job — otherwise
  // the adaptive loop and its visibility listener outlive the UI and keep
  // hitting the progress endpoint until the job reaches a terminal state.
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const fetchProgress = useCallback(
    async (jobId: string) => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`/api/v1/restore/jobs/${jobId}/progress`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Restore job not found');
          }
          throw new Error('Failed to fetch progress');
        }

        const data = await response.json();

        const elapsed = startTimeRef.current
          ? Math.floor((Date.now() - startTimeRef.current) / 1000)
          : 0;

        setState((prev) => ({
          ...prev,
          status: data.status,
          progress: data.progress_percent || 0,
          currentFile: data.current_file || null,
          filesRestored: data.files_restored || 0,
          dirsCreated: data.dirs_created || 0,
          totalFiles: data.total_files || 0,
          bytesWritten: data.bytes_written || 0,
          error: data.error_message || null,
          duration:
            data.status === 'COMPLETED' || data.status === 'FAILED' ? elapsed : prev.duration,
        }));

        // Stop polling if job is complete or failed
        if (data.status === 'COMPLETED' || data.status === 'FAILED') {
          stopPolling();
        }

        return data;
      } catch {
        // Don't stop polling on error, retry next interval
        return null;
      }
    },
    [stopPolling]
  );

  const startPolling = useCallback(
    (jobId: string) => {
      // Stop any existing polling
      stopPolling();

      // Reset start time
      startTimeRef.current = Date.now();

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      // Adaptive cadence: 4s while progress keeps changing, easing off to 20s
      // once it stalls, and parked entirely while the tab is hidden. The old
      // fixed 2s interval cost 30 req/min per open modal against an
      // organization budget of 1000/hour shared by every member.
      let intervalMs = RESTORE_POLL_BASE_MS;
      let lastSnapshot = '';
      let stopped = false;

      const schedule = () => {
        if (stopped) return;
        pollIntervalRef.current = setTimeout(tick, intervalMs);
      };

      const tick = async () => {
        if (stopped) return;
        if (document.hidden) {
          // Stay parked while hidden; the visibility listener below resumes.
          schedule();
          return;
        }
        const data = await fetchProgress(jobId);
        if (stopped) return;
        const snapshot = data
          ? `${data.status}:${data.progress_percent ?? ''}:${data.files_restored ?? ''}`
          : 'error';
        if (snapshot === lastSnapshot) {
          intervalMs = Math.min(RESTORE_POLL_MAX_MS, Math.round(intervalMs * RESTORE_POLL_BACKOFF));
        } else {
          intervalMs = RESTORE_POLL_BASE_MS;
        }
        lastSnapshot = snapshot;
        // fetchProgress stops polling itself once the job finishes.
        schedule();
      };

      const onVisibility = () => {
        if (stopped || document.hidden) return;
        intervalMs = RESTORE_POLL_BASE_MS;
        clearTimeout(pollIntervalRef.current);
        void tick();
      };
      document.addEventListener('visibilitychange', onVisibility);

      // Detach the listener when polling is torn down.
      stopListenersRef.current = () => {
        stopped = true;
        document.removeEventListener('visibilitychange', onVisibility);
      };

      void tick();
    },
    [fetchProgress, stopPolling]
  );

  const createRestoreJob = useCallback(
    async (request: CreateRestoreRequest): Promise<string | null> => {
      try {
        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        const token = localStorage.getItem('auth_token');
        const response = await fetch('/api/v1/restore/jobs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(request),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create restore job');
        }

        const data = await response.json();

        setState((prev) => ({
          ...prev,
          id: data.id,
          isLoading: false,
        }));

        // Start polling for progress
        startPolling(data.id);

        return data.id;
      } catch (err) {
        const error = err instanceof Error ? err.message : 'Failed to start restore';
        setState((prev) => ({ ...prev, error, isLoading: false }));
        return null;
      }
    },
    [startPolling]
  );

  const reset = useCallback(() => {
    stopPolling();
    startTimeRef.current = null;
    setState(initialState);
  }, [stopPolling]);

  return {
    ...state,
    createRestoreJob,
    startPolling,
    stopPolling,
    reset,
    getElapsedTime,
  };
}
